// Marker rendering: a coloured AwesomeMarkers pin carrying the POI category glyph.

// @deno-types="npm:@types/leaflet@^1.9.12"
import type * as Leaflet from "leaflet";
import { awesomeMarkers, leaflet } from "./leaflet.ts";
import { state, syncToUrl } from "../../state.ts";
import { ui } from "../../ui.ts";
import {
  DEFAULT_COLOR,
  DEFAULT_FEATURE,
  iconFor,
} from "../../features.ts";
import type { Marker } from "../../types.ts";
import { markElement } from "./editor.ts";
import { featureTarget } from "./context.ts";
import { MarkerEditor } from "./marker-editor.ts";
import { renumberMarkers } from "./marker-state.ts";

// How far above the marker position the label tooltip sits, clearing the pin.
const MARKER_TOOLTIP_ANCHOR: [number, number] = [0, -38];
// Markers render above text labels so a nearby label can't hide the pin.
const MARKER_Z_INDEX = 1000;

// Build a coloured pin containing the feature's Font Awesome glyph. The tooltip
// anchor lifts the label above the pin so the pin isn't covered by its own label.
function awesomeIcon(glyph: string, color: string): Leaflet.Icon {
  const icon = awesomeMarkers.icon({
    icon: glyph,
    prefix: "fa",
    markerColor: color,
  });
  (icon.options as { tooltipAnchor?: Leaflet.PointExpression }).tooltipAnchor =
    MARKER_TOOLTIP_ANCHOR;
  return icon;
}

// The pin for a marker's current feature/colour (falling back to the defaults).
function pinIcon(marker: Marker): Leaflet.Icon {
  return awesomeIcon(
    iconFor(marker.feature ?? DEFAULT_FEATURE),
    marker.color ?? DEFAULT_COLOR,
  );
}

export function addMarkerLayer(map: Leaflet.Map, marker: Marker): void {
  const layer = leaflet.marker([marker.lat, marker.lng], {
    icon: pinIcon(marker),
    zIndexOffset: MARKER_Z_INDEX,
  });
  const editor = new MarkerEditor(layer, marker, pinIcon.bind(null, marker));
  editor.wire();
  layer.addTo(featureTarget(map));
  markElement(layer, "marker");
}

// Append a marker at the clicked point using the toolbar's selected icon/colour.
export function placeMarker(map: Leaflet.Map, point: Leaflet.LatLng): void {
  const marker: Marker = {
    lat: point.lat,
    lng: point.lng,
    feature: ui.selectedFeature,
    color: ui.selectedColor,
  };
  // Only carry the flag when hover-only; leaving the key off (rather than setting
  // undefined) keeps the default out of the encoded URL.
  if (ui.selectedHoverLabel) {
    marker.hoverLabel = true;
  }
  state.markers = state.markers ?? [];
  state.markers.push(marker);
  // In numbered mode the new pin's label is its 1-based position (the next
  // number); renumber covers it and is a no-op otherwise.
  renumberMarkers();
  addMarkerLayer(map, marker);
  syncToUrl();
}
